"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { apiClient } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import Link from "next/link";
import { AuthHeader } from "@/components/auth-header";
import { Feedback } from "@/components/feedback";
import { usePageTitle } from "@/hooks/usePageTitle";
import { EnvelopeIcon, ArrowClockwiseIcon } from "@phosphor-icons/react/dist/ssr";
import { AuthHoneypotField } from "@/components/auth-honeypot-field";
import {
    honeypotRejectDelayMs,
    isHoneypotTripped,
} from "@/lib/honeypot";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState("");
    const [honeypot, setHoneypot] = useState("");
    const t = useTranslations();

    usePageTitle("pageTitles.forgotPassword");

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        // Validation
        if (!email.trim()) {
            setError(t("auth.emailRequired"));
            return;
        }

        if (!validateEmail(email)) {
            setError(t("auth.invalidEmailAddress"));
            return;
        }

        if (isHoneypotTripped(honeypot)) {
            setLoading(true);
            try {
                await new Promise((r) =>
                    setTimeout(r, honeypotRejectDelayMs())
                );
                setSuccess(true);
                setEmail("");
            } finally {
                setLoading(false);
            }
            return;
        }

        setLoading(true);

        try {
            await apiClient.sendResetEmail({ email });
            setSuccess(true);
            setEmail(""); // Clear the form
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : t("auth.failedToSendResetEmail")
            );
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="grid w-full grid-cols-1 gap-8">
                <AuthHeader
                    heading={t("auth.resetEmailSent")}
                    subheading={t("auth.resetEmailSentDescription")}
                />
                <Button
                    variant="outline"
                    onClick={() => setSuccess(false)}
                    className="w-full"
                >
                    <ArrowClockwiseIcon weight="bold" className="size-5" />
                    {t("common.retry")}
                </Button>
                <Separator className="my-2" />
                <p className="text-sm text-muted-foreground">
                    {t("auth.alreadyHaveAccount")}{" "}
                    <Link
                        href="/auth/signin/email"
                        className="font-semibold text-primary hover:underline"
                    >
                        {t("auth.signIn")}
                    </Link>
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="relative grid w-full grid-cols-1 gap-8"
        >
            <AuthHoneypotField
                value={honeypot}
                onChange={setHoneypot}
            />
            <AuthHeader
                heading={t("auth.forgotPassword")}
                subheading={t("auth.forgotPasswordDescription")}
            />

            {error && <Feedback message={error} status="error" />}

            <div className="space-y-2">
                <Label>{t("common.email")}</Label>
                <Input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                />
            </div>
            <Button
                type="submit"
                variant="default"
                className="w-full"
                disabled={loading || !email.trim()}
            >
                <EnvelopeIcon weight="bold" className="size-5" />
                {loading
                    ? t("auth.sendingResetEmail")
                    : t("auth.sendResetEmail")}
            </Button>
            <p className="text-sm text-muted-foreground">
                {t("auth.alreadyHaveAccount")}{" "}
                <Link
                    href="/auth/signin/email"
                    className="font-semibold text-primary hover:underline"
                >
                    {t("auth.signIn")}
                </Link>
            </p>
        </form>
    );
}
